<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadFile } from '@chatwoot/utils';
import Icon from 'next/icon/Icon.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { isMediaAttachment, normalizeAttachment } from './galleryUtils';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [Object, String],
    default: null,
  },
});

const { t } = useI18n();
const showGallery = ref(false);
const activeAttachment = ref(null);
const currentPage = ref(1);
const PAGE_SIZE = 12;

const mediaAttachments = computed(() =>
  props.attachments.filter(isMediaAttachment)
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(mediaAttachments.value.length / PAGE_SIZE))
);
const visibleAttachments = computed(() => {
  const startIndex = (currentPage.value - 1) * PAGE_SIZE;
  return mediaAttachments.value.slice(startIndex, startIndex + PAGE_SIZE);
});
const pageIndicator = computed(
  () => `${currentPage.value} / ${totalPages.value}`
);

const openGallery = attachment => {
  activeAttachment.value = normalizeAttachment(attachment);
  showGallery.value = true;
};

const downloadAttachment = async attachment => {
  try {
    await downloadFile({
      url: attachment.data_url,
      type: attachment.file_type,
      extension: attachment.extension,
    });
  } catch {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  }
};

const isVideoAttachment = attachment =>
  ['video', 'ig_reel'].includes(attachment.file_type);

const isPreviewableMedia = attachment =>
  attachment.file_type === 'image' || isVideoAttachment(attachment);

const goToPreviousPage = () => {
  if (currentPage.value <= 1) return;
  currentPage.value -= 1;
};

const goToNextPage = () => {
  if (currentPage.value >= totalPages.value) return;
  currentPage.value += 1;
};

watch(totalPages, newTotalPages => {
  if (currentPage.value > newTotalPages) {
    currentPage.value = newTotalPages;
  }
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-if="loading && !mediaAttachments.length"
      class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
    >
      <span class="text-sm">{{ $t('GALLERY_VIEW.LOADING') }}</span>
      <Spinner class="size-5" />
    </div>
    <p v-else-if="error && !mediaAttachments.length">
      {{ $t('GALLERY_VIEW.ERROR_LOADING_ATTACHMENTS') }}
    </p>
    <div v-else class="flex flex-col gap-2">
      <div
        v-if="loading"
        class="flex items-center justify-center gap-2 text-xs text-n-slate-11"
      >
        <Spinner class="size-4" />
        <span>{{ $t('GALLERY_VIEW.LOADING') }}</span>
      </div>

      <div
        v-if="!visibleAttachments.length"
        class="flex items-center justify-center rounded-md border border-dashed border-n-weak p-6 text-sm text-n-slate-11"
      >
        {{ $t('GALLERY_VIEW.NO_ATTACHMENTS') }}
      </div>

      <div v-else class="grid grid-cols-3 gap-1">
        <button
          v-for="attachment in visibleAttachments"
          :key="attachment.id"
          type="button"
          class="relative aspect-square overflow-hidden cursor-pointer rounded-sm group"
          @click="
            isPreviewableMedia(attachment)
              ? openGallery(attachment)
              : downloadAttachment(attachment)
          "
        >
          <template v-if="isPreviewableMedia(attachment)">
            <img
              v-if="attachment.file_type === 'image'"
              :src="attachment.thumb_url || attachment.data_url"
              class="w-full h-full object-cover"
            />
            <video
              v-else
              :src="attachment.data_url"
              class="w-full h-full object-cover"
              muted
              playsinline
              preload="metadata"
            />
            <div
              v-if="isVideoAttachment(attachment)"
              class="absolute inset-0 flex items-center justify-center w-full h-full p-1"
            >
              <div
                class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
              >
                <Icon
                  icon="i-teenyicons-play-small-solid"
                  class="size-7 text-n-slate-12/80 backdrop-blur"
                />
              </div>
            </div>
          </template>
          <div
            v-else
            class="flex items-center justify-center w-full h-full bg-n-slate-3"
          >
            <div
              class="flex items-center justify-center size-14 rounded-xl bg-n-slate-1 shadow-sm"
            >
              <FileIcon
                :file-type="attachment.extension || attachment.file_type"
                class="size-8 text-n-slate-11"
              />
            </div>
          </div>
        </button>
        <GalleryView
          v-if="showGallery && activeAttachment"
          v-model:show="showGallery"
          :attachment="activeAttachment"
          :all-attachments="mediaAttachments.map(normalizeAttachment)"
          @close="showGallery = false"
        />
      </div>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
      >
        <button
          type="button"
          class="grid place-content-center size-8 rounded-md border border-n-weak disabled:opacity-50"
          :disabled="currentPage === 1"
          @click="goToPreviousPage"
        >
          <Icon icon="i-lucide-chevron-left" class="size-4" />
        </button>
        <span>{{ pageIndicator }}</span>
        <button
          type="button"
          class="grid place-content-center size-8 rounded-md border border-n-weak disabled:opacity-50"
          :disabled="currentPage === totalPages"
          @click="goToNextPage"
        >
          <Icon icon="i-lucide-chevron-right" class="size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
